import type { Condition } from '@medplum/fhirtypes';
import React from 'react';

import { Grid, Icon, Stack, Tag, Text } from '@eluve/components';
import { type FhirPageControls, LoadMoreRow } from '@eluve/fhir-data';
import {
  FhirStatusTag,
  formatFhirDate,
  resourceDisplayName,
} from '@eluve/fhir-renderer';

import { type ProblemList, conditionToken, icd10Code } from '../problem-list';
import { isRefuted } from '../review-markers';

/**
 * The Overview-tab Conditions card (ENG-5365): the patient's problem list —
 * pending AI drafts and current problems first, past (resolved / inactive)
 * conditions below. Rows are composed from renderer primitives
 * (`resourceDisplayName`, `FhirStatusTag`, `formatFhirDate`); interactivity
 * and the ICD-10 / Draft badging are app chrome, per the renderer's
 * presentational contract.
 */
export type ConditionsCardProps = {
  list: ProblemList;
  loading: boolean;
  failed: boolean;
  paging: FhirPageControls;
  /** Open the detail drawer for a condition (routing owned by the section). */
  onOpenCondition: (token: string) => void;
};

const CenteredNote: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Grid place="center" className="px-6 py-12">
    <Text size="sm" color="tertiary" weight="medium">
      {children}
    </Text>
  </Grid>
);

const ConditionRows: React.FC<{
  conditions: Condition[];
  pending?: boolean;
  onOpenCondition: (token: string) => void;
}> = ({ conditions, pending = false, onOpenCondition }) => (
  <>
    {conditions.map((condition) => {
      const icd10 = icd10Code(condition);
      const status = condition.clinicalStatus?.coding?.[0]?.code;
      const date = formatFhirDate(
        condition.onsetDateTime ?? condition.recordedDate,
      );
      return (
        <button
          key={conditionToken(condition)}
          type="button"
          onClick={() => onOpenCondition(conditionToken(condition))}
          data-testid="condition-row"
          className="relative flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-left not-first:before:absolute not-first:before:inset-x-1 not-first:before:top-0 not-first:before:border-t not-first:before:border-border-primary hover:bg-bg-secondary not-first:hover:before:opacity-0 [:hover+&]:before:opacity-0"
        >
          <div className="min-w-0 flex-1">
            <Stack
              direction="horizontal"
              gap={2}
              align="center"
              fullWidth={false}
              className="min-h-6"
            >
              <Text inline size="sm" weight="medium" className="truncate">
                {resourceDisplayName(condition)}
              </Text>
              {status && status !== 'active' && (
                <FhirStatusTag status={status} />
              )}
              {icd10 && (
                <Tag pill subtle size="xs" color="lightblue" label={icd10} />
              )}
              {pending && (
                <Tag
                  pill
                  subtle
                  size="xs"
                  color="insights"
                  label={isRefuted(condition) ? 'Denial · Draft' : 'Draft'}
                />
              )}
            </Stack>
            {date && (
              <Text
                size="xs"
                color="tertiary"
                weight="medium"
                className="mt-0.5"
              >
                {condition.onsetDateTime
                  ? `Onset: ${date}`
                  : `Recorded: ${date}`}
              </Text>
            )}
          </div>
          <Icon
            name="ChevronRight"
            size="xs"
            className="shrink-0 text-text-tertiary"
          />
        </button>
      );
    })}
  </>
);

export const ConditionsCard: React.FC<ConditionsCardProps> = ({
  list,
  loading,
  failed,
  paging,
  onOpenCondition,
}) => {
  const isEmpty =
    list.pending.length === 0 &&
    list.active.length === 0 &&
    list.past.length === 0;

  return (
    <section
      data-testid="conditions-card"
      className="rounded-lg bg-bg-primary shadow-xs ring-1 ring-black/5"
    >
      <Stack
        direction="horizontal"
        gap={2}
        align="center"
        justify="between"
        className="h-12 px-4"
      >
        <Text inline size="sm" weight="semibold">
          Conditions
        </Text>
      </Stack>

      {failed ? (
        <CenteredNote>Couldn't load conditions.</CenteredNote>
      ) : loading ? (
        <CenteredNote>Loading conditions…</CenteredNote>
      ) : isEmpty ? (
        <CenteredNote>No conditions recorded.</CenteredNote>
      ) : (
        <>
          <div className="p-1">
            <ConditionRows
              conditions={list.pending}
              pending
              onOpenCondition={onOpenCondition}
            />
            <ConditionRows
              conditions={list.active}
              onOpenCondition={onOpenCondition}
            />
            <ConditionRows
              conditions={list.past}
              onOpenCondition={onOpenCondition}
            />
          </div>
          <LoadMoreRow paging={paging} noun="conditions" />
        </>
      )}
    </section>
  );
};
